import React, { useEffect, useMemo, useState } from "react";
import { createDepartment, getDepartments } from "../../api/departments";
import { createEmployeeWithAccount } from "../../api/employees";
import { useAuth, DEFAULT_INITIAL_PASSWORD } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { resolveBackendDepartmentId } from "../../utils/employeeAccountHelpers";
import "./AdminAccountsManagement.css";

const ACCOUNT_TYPES = {
  gm: {
    roleSlug: "gm",
    label: "المدير العام",
    jobTitle: "المدير العام",
    description: "حساب واحد فقط — أعلى صلاحية تشغيلية بعد الأدمن",
    department: "hr",
  },
  owner: {
    roleSlug: "owner",
    label: "مالك الشركة",
    jobTitle: "مالك الشركة",
    description:
      "حسابات متعددة — يشاهد كل ما يحدث في الشركة بدون موافقة أو رفض أو تعديل",
    department: "hr",
    singleton: false,
  },
  hr: {
    roleSlug: "hr",
    label: "الموارد البشرية",
    jobTitle: "مسؤول موارد بشرية",
    description: "حساب واحد فقط — يدير الموظفين ويوافق على الطلبات والمرتبات",
    department: "hr",
    singleton: true,
  },
  hrStaff: {
    roleSlug: "hr_staff",
    label: "موظف موارد بشرية",
    jobTitle: "موظف موارد بشرية",
    description:
      "حسابات متعددة — نفس واجهة الـ HR بدون المرتبات وبدون صلاحية الموافقة على الطلبات",
    department: "hr",
    singleton: false,
  },
  generalAccountant: {
    roleSlug: "employee",
    label: "المحاسب العام",
    jobTitle: "محاسب عام",
    description:
      "حساب واحد فقط — يوجّه صرف السلف لمسؤول الخزنة أو كاشيرية الفروع",
    department: "accounts",
  },
  treasuryOfficer: {
    roleSlug: "employee",
    label: "مسؤول الخزنة",
    jobTitle: "مسؤول الخزنة",
    description: "حساب واحد فقط — يستلم توجيه صرف السلف العادية والمقسطة",
    department: "accounts",
  },
  cashierManager: {
    roleSlug: "manager",
    label: "مدير الكاشيرية",
    jobTitle: "مدير الكاشيرية",
    description:
      "حساب واحد فقط — يتبع رئيس قسم الحسابات ويُشرف على كل كاشيرية الفروع (الكاشير يظل تحته)",
    department: "accounts",
  },
};

const EMPTY_FORM = {
  fullName: "",
  email: "",
  employeeCode: "",
  username: "",
};

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
const EMPLOYEE_CODE_REGEX = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

function extractErrorMessage(err, fallback) {
  const message =
    err?.response?.data?.message || err?.response?.data?.error || fallback;
  return Array.isArray(message) ? message.join("\n") : message;
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeDepartments(payload) {
  return unwrapList(payload).map((d) => ({
    id: d.id,
    backendId: d.id,
    code: d.code,
    name: d.name,
  }));
}

function findHrDepartmentId(departments = []) {
  return (
    resolveBackendDepartmentId("hr-dept", departments) ||
    departments.find((d) => d.code === "HR")?.backendId ||
    departments.find((d) => d.code === "HR")?.id ||
    null
  );
}

function findAccountsDepartmentId(departments = []) {
  return (
    resolveBackendDepartmentId("accounts", departments) ||
    departments.find((d) => d.code === "ACC")?.backendId ||
    departments.find((d) => d.code === "ACC")?.id ||
    null
  );
}

function findExistingAccount(employees, key) {
  const active = employees.filter((e) => e.status !== "terminated");
  if (key === "gm") {
    return active.find((e) => e.roleSlugs?.includes("gm")) || null;
  }
  if (key === "owner" || key === "hrStaff") {
    // Multi-account roles — not a singleton blocker
    return null;
  }
  if (key === "hr") {
    return active.find((e) => e.roleSlugs?.includes("hr")) || null;
  }
  if (key === "generalAccountant") {
    return (
      active.find((e) => e.jobTitle === ACCOUNT_TYPES.generalAccountant.jobTitle) ||
      null
    );
  }
  if (key === "treasuryOfficer") {
    return (
      active.find((e) => e.jobTitle === ACCOUNT_TYPES.treasuryOfficer.jobTitle) ||
      null
    );
  }
  if (key === "cashierManager") {
    return (
      active.find((e) => e.jobTitle === ACCOUNT_TYPES.cashierManager.jobTitle) ||
      null
    );
  }
  return null;
}

function countHrStaffAccounts(employees = []) {
  return employees.filter(
    (e) =>
      e.status !== "terminated" && e.roleSlugs?.includes("hr_staff"),
  ).length;
}

function countOwnerAccounts(employees = []) {
  return employees.filter(
    (e) => e.status !== "terminated" && e.roleSlugs?.includes("owner"),
  ).length;
}

function isMultiAccountKey(key) {
  return key === "hrStaff" || key === "owner";
}

function AdminAccountsManagement() {
  const { user } = useAuth();
  const { departments, getAllEmployees, refreshData } = useData();
  const [accountKey, setAccountKey] = useState("gm");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [lastCredentials, setLastCredentials] = useState(null);
  const [fetchedDepartments, setFetchedDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState(null);

  const isAdmin = Boolean(
    user?.role === "admin" || user?.backendRoles?.includes("admin"),
  );

  const employees = useMemo(() => getAllEmployees?.() || [], [getAllEmployees]);

  const existingByRole = useMemo(() => {
    return {
      gm: findExistingAccount(employees, "gm"),
      owner: null,
      hr: findExistingAccount(employees, "hr"),
      hrStaff: null,
      generalAccountant: findExistingAccount(employees, "generalAccountant"),
      treasuryOfficer: findExistingAccount(employees, "treasuryOfficer"),
      cashierManager: findExistingAccount(employees, "cashierManager"),
    };
  }, [employees]);

  const hrStaffCount = useMemo(
    () => countHrStaffAccounts(employees),
    [employees],
  );

  const ownerCount = useMemo(
    () => countOwnerAccounts(employees),
    [employees],
  );

  const primaryHrEmployee = useMemo(
    () =>
      employees.find(
        (e) => e.status !== "terminated" && e.roleSlugs?.includes("hr"),
      ) || null,
    [employees],
  );

  const mergedDepartments = useMemo(() => {
    const byId = new Map();
    [...(departments || []), ...fetchedDepartments].forEach((dept) => {
      const key = dept.backendId || dept.id;
      if (key) byId.set(key, dept);
    });
    return [...byId.values()];
  }, [departments, fetchedDepartments]);

  const hrDepartmentId = useMemo(
    () => findHrDepartmentId(mergedDepartments),
    [mergedDepartments],
  );

  const accountsDepartmentId = useMemo(
    () => findAccountsDepartmentId(mergedDepartments),
    [mergedDepartments],
  );

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;
    setDeptLoading(true);
    setDeptError(null);

    getDepartments()
      .then((payload) => {
        if (cancelled) return;
        setFetchedDepartments(normalizeDepartments(payload));
      })
      .catch((err) => {
        if (cancelled) return;
        setDeptError(
          extractErrorMessage(err, "تعذر تحميل الإدارات من الخادم"),
        );
      })
      .finally(() => {
        if (!cancelled) setDeptLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const selected = ACCOUNT_TYPES[accountKey];
  const existing = existingByRole[accountKey];
  const canCreate =
    isAdmin &&
    !submitting &&
    (isMultiAccountKey(accountKey) || !existing);

  function handleFieldChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setLastCredentials(null);
  }

  async function ensureDepartmentId(kind) {
    const finder =
      kind === "accounts" ? findAccountsDepartmentId : findHrDepartmentId;
    const defaults =
      kind === "accounts"
        ? {
            name: "الحسابات",
            code: "ACC",
            description: "Accounts",
            isOperational: false,
          }
        : {
            name: "الموارد البشرية",
            code: "HR",
            description: "Human Resources",
            isOperational: false,
          };

    let id = finder(mergedDepartments);
    if (id) return id;

    const listed = normalizeDepartments(await getDepartments());
    setFetchedDepartments(listed);
    id = finder(listed);
    if (id) return id;

    try {
      const created = await createDepartment(defaults);
      const row = {
        id: created.id,
        backendId: created.id,
        code: created.code || defaults.code,
        name: created.name || defaults.name,
      };
      setFetchedDepartments((prev) => [...prev, row]);
      return created.id;
    } catch (err) {
      const retry = normalizeDepartments(await getDepartments());
      setFetchedDepartments(retry);
      id = finder(retry);
      if (id) return id;
      throw err;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!isAdmin) {
      alert("إنشاء هذه الحسابات مسموح لحساب الأدمن فقط");
      return;
    }

    if (existing && !isMultiAccountKey(accountKey)) {
      alert(`يوجد حساب ${selected.label} بالفعل (${existing.name})`);
      return;
    }

    if (accountKey === "hrStaff" && !primaryHrEmployee?.id) {
      alert(
        "يجب إنشاء حساب الموارد البشرية (HR) أولاً ليكون المدير المباشر لموظف الموارد البشرية",
      );
      return;
    }

    const fullName = form.fullName.trim();
    if (!fullName) {
      alert("الاسم الكامل مطلوب");
      return;
    }

    const email = form.email.trim().toLowerCase() || undefined;
    const employeeCodeRaw = form.employeeCode.trim().toUpperCase();
    const usernameRaw = form.username.trim();

    if (employeeCodeRaw && !EMPLOYEE_CODE_REGEX.test(employeeCodeRaw)) {
      alert(
        "كود الموظف غير صالح. استخدم حروفاً وأرقاماً وشرطة فقط (مثال: GM-001)",
      );
      return;
    }

    let username;
    if (usernameRaw) {
      if (!USERNAME_REGEX.test(usernameRaw)) {
        alert(
          "اسم المستخدم يجب أن يكون إنجليزي فقط (حروف، أرقام، _) بدون مسافات.\nاتركه فارغاً ليُنشأ تلقائياً.",
        );
        return;
      }
      username = usernameRaw.toLowerCase();
    }

    setSubmitting(true);
    try {
      const deptKind = selected.department === "accounts" ? "accounts" : "hr";
      let departmentId;
      try {
        departmentId = await ensureDepartmentId(deptKind);
      } catch {
        departmentId = undefined;
      }

      if (selected.department === "accounts" && !departmentId) {
        alert("تعذر تجهيز إدارة الحسابات — أعد المحاولة أو أنشئ الإدارة يدوياً");
        setSubmitting(false);
        return;
      }

      const result = await createEmployeeWithAccount({
        fullName,
        ...(email ? { email } : {}),
        ...(employeeCodeRaw ? { employeeCode: employeeCodeRaw } : {}),
        ...(username ? { username } : {}),
        password: DEFAULT_INITIAL_PASSWORD,
        roleSlug: selected.roleSlug,
        jobTitle: selected.jobTitle,
        ...(departmentId ? { departmentId } : {}),
        ...(accountKey === "hrStaff" && primaryHrEmployee?.id
          ? { managerId: primaryHrEmployee.id }
          : {}),
        hireDate: new Date().toISOString().slice(0, 10),
      });

      const credentials = result?.loginCredentials;
      const code = credentials?.employeeCode || result?.employeeCode || "—";
      const loginUsername = credentials?.username || username || "—";
      const loginEmail = credentials?.email || email;

      setLastCredentials({
        roleLabel: selected.label,
        employeeCode: code,
        username: loginUsername,
        email: loginEmail,
        password: DEFAULT_INITIAL_PASSWORD,
      });

      setForm(EMPTY_FORM);
      setDeptError(null);
      await refreshData();
    } catch (err) {
      alert(
        extractErrorMessage(
          err,
          "تعذر إنشاء الحساب — تحقق من البيانات أو صلاحياتك",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="admin-accounts" dir="rtl">
        <div className="admin-accounts-denied">
          هذه الصفحة مخصصة لحساب أدمن النظام فقط.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-accounts" dir="rtl">
      <header className="admin-accounts-header">
        <h1 className="admin-accounts-title">إنشاء حسابات القيادة والحسابات</h1>
        <p className="admin-accounts-subtitle">
          أنشئ حساب المدير العام وملاك الشركة والموارد البشرية وموظفي الموارد
          البشرية والحسابات. حسابات GM/HR/المحاسبين فردية؛ ملاك الشركة وموظفو
          الموارد البشرية متعددون. لإضافة باقي الموظفين والأدوار استخدم قائمة
          «الموظفين».
        </p>
      </header>

      {(deptLoading || deptError) && (
        <div
          className={`admin-accounts-warning${deptError ? " is-error" : ""}`}
        >
          {deptLoading ? "جارٍ تحميل الإدارات..." : deptError}
        </div>
      )}

      {!deptLoading && !deptError && !hrDepartmentId && (
        <div className="admin-accounts-warning">
          إدارة الموارد البشرية ستُنشأ تلقائياً عند إنشاء حساب GM/HR إن لزم.
        </div>
      )}

      {!deptLoading && !deptError && !accountsDepartmentId && (
        <div className="admin-accounts-warning">
          إدارة الحسابات ستُنشأ تلقائياً عند إنشاء حساب المحاسب العام أو مسؤول
          الخزنة إن لزم.
        </div>
      )}

      <div className="admin-accounts-status-grid">
        {Object.entries(ACCOUNT_TYPES).map(([key, config]) => {
          const account = existingByRole[key];
          const isMulti = isMultiAccountKey(key);
          const multiCount = key === "owner" ? ownerCount : hrStaffCount;
          return (
            <div
              key={key}
              className={`admin-accounts-status-card${
                account || (isMulti && multiCount > 0) ? " is-ready" : ""
              }`}
            >
              <div className="admin-accounts-status-label">{config.label}</div>
              {isMulti ? (
                <>
                  <div className="admin-accounts-status-name">
                    {multiCount > 0
                      ? `${multiCount} حساب`
                      : "لا يوجد بعد"}
                  </div>
                  <div className="admin-accounts-status-meta">
                    {config.description}
                  </div>
                  <span
                    className={`admin-accounts-badge ${
                      multiCount > 0 ? "ready" : "pending"
                    }`}
                  >
                    {multiCount > 0 ? "متاح للإضافة" : "مطلوب"}
                  </span>
                </>
              ) : account ? (
                <>
                  <div className="admin-accounts-status-name">{account.name}</div>
                  <div className="admin-accounts-status-meta">
                    {account.code}
                    {account.email ? ` · ${account.email}` : ""}
                  </div>
                  <span className="admin-accounts-badge ready">تم الإنشاء</span>
                </>
              ) : (
                <>
                  <div className="admin-accounts-status-name">غير موجود</div>
                  <div className="admin-accounts-status-meta">
                    {config.description}
                  </div>
                  <span className="admin-accounts-badge pending">مطلوب</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="admin-accounts-form-card">
        <div className="admin-accounts-role-tabs">
          {Object.entries(ACCOUNT_TYPES).map(([key, config]) => (
            <button
              key={key}
              type="button"
              className={`admin-accounts-role-tab${accountKey === key ? " is-active" : ""}`}
              onClick={() => {
                setAccountKey(key);
                setLastCredentials(null);
              }}
            >
              {config.label}
              {key === "hrStaff"
                ? hrStaffCount > 0
                  ? ` (${hrStaffCount})`
                  : ""
                : key === "owner"
                  ? ownerCount > 0
                    ? ` (${ownerCount})`
                    : ""
                : existingByRole[key]
                  ? " ✓"
                  : ""}
            </button>
          ))}
        </div>

        {existing && !isMultiAccountKey(accountKey) ? (
          <div className="admin-accounts-exists">
            حساب {selected.label} موجود بالفعل:
            <strong> {existing.name}</strong>
            {existing.code ? ` (${existing.code})` : ""}.
            لا يمكن إنشاء حساب ثانٍ لهذا الدور.
          </div>
        ) : (
          <form className="admin-accounts-form" onSubmit={handleSubmit}>
            <p className="admin-accounts-form-hint">{selected.description}</p>
            {accountKey === "hrStaff" && primaryHrEmployee ? (
              <p className="admin-accounts-form-hint">
                المدير المباشر الافتراضي: {primaryHrEmployee.name} (
                {primaryHrEmployee.code})
              </p>
            ) : null}

            <div className="admin-accounts-field">
              <label className="admin-accounts-label" htmlFor="admin-fullName">
                الاسم الكامل
              </label>
              <input
                id="admin-fullName"
                className="admin-accounts-input"
                value={form.fullName}
                onChange={(e) => handleFieldChange("fullName", e.target.value)}
                placeholder={
                  accountKey === "gm"
                    ? "مثال: أحمد المدير العام"
                    : accountKey === "owner"
                      ? "مثال: مالك الشركة"
                      : "مثال: سارة مسؤولة الموارد البشرية"
                }
                required
                disabled={submitting}
              />
            </div>

            <div className="admin-accounts-field-row">
              <div className="admin-accounts-field">
                <label className="admin-accounts-label" htmlFor="admin-email">
                  البريد الإلكتروني{" "}
                  <span className="admin-accounts-optional">(اختياري)</span>
                </label>
                <input
                  id="admin-email"
                  type="email"
                  className="admin-accounts-input"
                  value={form.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  placeholder={
                    accountKey === "gm"
                      ? "gm@company.com"
                      : accountKey === "owner"
                        ? "owner@company.com"
                        : "hr@company.com"
                  }
                  disabled={submitting}
                />
              </div>

              <div className="admin-accounts-field">
                <label className="admin-accounts-label" htmlFor="admin-code">
                  كود الموظف{" "}
                  <span className="admin-accounts-optional">
                    (اختياري — يُنشأ تلقائياً)
                  </span>
                </label>
                <input
                  id="admin-code"
                  className="admin-accounts-input"
                  value={form.employeeCode}
                  onChange={(e) =>
                    handleFieldChange("employeeCode", e.target.value)
                  }
                  placeholder={
                    accountKey === "gm"
                      ? "GM-001"
                      : accountKey === "owner"
                        ? "OWN-001"
                        : "HR-001"
                  }
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="admin-accounts-field">
              <label className="admin-accounts-label" htmlFor="admin-username">
                اسم المستخدم{" "}
                <span className="admin-accounts-optional">
                  (اختياري — إنجليزي فقط بدون مسافات)
                </span>
              </label>
              <input
                id="admin-username"
                className="admin-accounts-input"
                value={form.username}
                onChange={(e) => handleFieldChange("username", e.target.value)}
                placeholder={accountKey === "gm" ? "gm_ahmed" : accountKey === "owner" ? "owner_ahmed" : "hr_sara"}
                disabled={submitting}
                dir="ltr"
              />
            </div>

            <div className="admin-accounts-password-note">
              كلمة المرور الابتدائية:{" "}
              <code>{DEFAULT_INITIAL_PASSWORD}</code> — يُطلب تغييرها عند أول
              دخول
            </div>

            <button
              type="submit"
              className="admin-accounts-submit"
              disabled={!canCreate}
            >
              {submitting
                ? "جارٍ الإنشاء..."
                : `إنشاء حساب ${selected.label}`}
            </button>
          </form>
        )}

        {lastCredentials && (
          <div className="admin-accounts-credentials">
            <h3>تم إنشاء حساب {lastCredentials.roleLabel}</h3>
            <ul>
              <li>
                كود الموظف: <strong>{lastCredentials.employeeCode}</strong>
              </li>
              <li>
                اسم المستخدم: <strong>{lastCredentials.username}</strong>
              </li>
              {lastCredentials.email && (
                <li>
                  البريد: <strong>{lastCredentials.email}</strong>
                </li>
              )}
              <li>
                كلمة المرور: <strong>{lastCredentials.password}</strong>
              </li>
            </ul>
            <p>يمكن تسجيل الدخول بكود الموظف أو اسم المستخدم أو البريد.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAccountsManagement;
